import moment from "moment";
import { contactUSkeys, TAB_KEYS, vacancyKeys, contract, aboutUSkeys } from "../config";

const isStoredMediaUrl = (url) =>
  typeof url === "string" &&
  url.length > 0 &&
  !url.startsWith("blob:") &&
  !url.startsWith("data:");

const getGifUploadFile = (file) => {
  if (!file || typeof file !== "object") return null;
  if (file.originFileObj) return getGifUploadFile(file.originFileObj);
  if (typeof File !== "undefined" && file instanceof File) return file;
  if (typeof Blob !== "undefined" && file instanceof Blob) return file;
  if (typeof file.size === "number" && file.size > 0 && typeof file.type === "string") {
    return file;
  }
  return null;
};

const buildGifInputPayload = ({ files, gifState, mediaEx, existingUrl }) => ({
  gifs: files,
  duration: gifState.frameDelay ?? mediaEx.frameDuration ?? 0,
  loopCount: gifState.loopCount ?? mediaEx.loopCount ?? 0,
  crossFadeSteps: gifState.crossfade
    ? gifState.crossfadeSteps ?? 5
    : mediaEx.crossfade
      ? mediaEx.crossfadeSteps ?? 5
      : 0,
  input: {
    mediaType: "GIF",
    url: isStoredMediaUrl(existingUrl) ? existingUrl : null,
    mediaProperty: existingUrl
      ? null
      : gifState.mediaProperty || mediaEx.mediaProperty || null,
  },
});

const isLocalGifPreviewUrl = (url) =>
  typeof url === "string" &&
  (url.startsWith("blob:") || url.startsWith("data:"));

export const ABOUT_US_GIF_TABS = [
  aboutUSkeys.About,
  aboutUSkeys["Our Story"],
  aboutUSkeys["Our Team"],
  aboutUSkeys["Our Client"],
  aboutUSkeys["Our Supporter"],
];

export const isAboutUsGifTab = (tabKey) => ABOUT_US_GIF_TABS.includes(tabKey);

export const ORG_ADMIN_GIF_TABS = [
  TAB_KEYS.corousel,
  TAB_KEYS.businessCard,
  TAB_KEYS.product,
  TAB_KEYS.service,
  TAB_KEYS.contract,
  TAB_KEYS.apprenticeship,
  TAB_KEYS.vacancy,
  TAB_KEYS.courses,
  TAB_KEYS.events,
  TAB_KEYS.whyWorkForUs,
  TAB_KEYS.teamsAndRoles,
  TAB_KEYS.news,
  TAB_KEYS.press,
];

export const isOrgAdminGifTab = (tabKey) =>
  isAboutUsGifTab(tabKey) || ORG_ADMIN_GIF_TABS.includes(tabKey);

const usesNullGifUnchangedPayload = (tabKey, aboutUsEnum) =>
  isOrgAdminGifTab(tabKey) || isAboutUsGifTab(aboutUsEnum);

const isReplacedAboutUsGif = (gifState, generatedFile) =>
  !!generatedFile &&
  (gifState.isUnsaved || isLocalGifPreviewUrl(gifState.previewUrl));

const applyGifMediaToTemp = (temp, mediaEx, { useNullUnchanged } = {}) => {
  const gifState = mediaEx.gifCreator || {};
  const generatedFile = getGifUploadFile(gifState.generatedFile);
  const sourceImages = (mediaEx.gifImages || gifState.images || []).filter(
    (img) => getGifUploadFile(img?.file) || getGifUploadFile(img)
  );
  const existingUrl = isStoredMediaUrl(mediaEx.url)
    ? mediaEx.url
    : isStoredMediaUrl(gifState.previewUrl)
      ? gifState.previewUrl
      : null;

  const replacedGif =
    useNullUnchanged && isReplacedAboutUsGif(gifState, generatedFile);
  if (generatedFile && (!existingUrl || gifState.isUnsaved || replacedGif)) {
    temp.gifInput = buildGifInputPayload({
      files: [{ file: generatedFile }],
      gifState,
      mediaEx,
      existingUrl:
        useNullUnchanged &&
        (gifState.isUnsaved || isLocalGifPreviewUrl(gifState.previewUrl))
          ? null
          : existingUrl,
    });
  } else if (!existingUrl && sourceImages.length >= 2 && gifState.isUnsaved) {
    temp.gifInput = buildGifInputPayload({
      files: sourceImages,
      gifState: {
        frameDelay: gifState.frameDelay ?? mediaEx.frameDuration,
        loopCount: gifState.loopCount ?? mediaEx.loopCount,
        crossfade: gifState.crossfade ?? mediaEx.crossfade,
        crossfadeSteps: gifState.crossfadeSteps ?? mediaEx.crossfadeSteps,
        mediaProperty: gifState.mediaProperty,
      },
      mediaEx,
      existingUrl,
    });
  } else if (existingUrl) {
    if (useNullUnchanged) {
      temp.gifInput = null;
    } else {
      temp.media = {
        mediaType: "GIF",
        url: existingUrl,
      };
    }
  }
};

const normalizeGifInputOnFormData = (formData, d) => {
  if (!d?.gifInput) return d;

  const gifs = d.gifInput.gifs || [];
  let appendedGifFiles = 0;

  gifs.forEach((gifImg) => {
    const file = getGifUploadFile(gifImg) || getGifUploadFile(gifImg?.file);
    if (file) {
      formData.append("gifs", file);
      appendedGifFiles += 1;
    }
  });

  d.gifInput = {
    input: {
      mediaType: d.gifInput.input?.mediaType || "GIF",
      url: isStoredMediaUrl(d.gifInput.input?.url) ? d.gifInput.input.url : null,
      mediaProperty: d.gifInput.input?.mediaProperty ?? null,
    },
  };

  if (appendedGifFiles > 0) {
    d.media = null;
  } else {
    d.gifInput = null;
    d.media = null;
  }

  return d;
};

const fileFromGifSource = async (url, fileName) => {
  if (!url || typeof url !== "string") return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob?.size) return null;
    let name = fileName || "about-us.gif";
    if (!/\.gif$/i.test(name)) name = `${name}.gif`;
    return new File([blob], name, { type: blob.type || "image/gif" });
  } catch (error) {
    return null;
  }
};

const hasGifBinaryToUpload = (d) =>
  (d?.gifInput?.gifs || []).some(
    (gifImg) => getGifUploadFile(gifImg) || getGifUploadFile(gifImg?.file)
  );

// About Us (Purpose/Story/Team/Clients/Supporters) update has no File after
// hydrate (BE returns a URL). Recover each newly generated GIF binary from
// live form state or a local blob preview. Do not fetch stored backend URLs
// — unchanged GIFs keep their BE references.
export const withAboutUsGifBinaryFiles = async (values, liveValues) => {
  if (!Array.isArray(values)) return values;

  return Promise.all(
    values.map(async (row, index) => {
      if (!row || row.mediaLink?.mediaType !== "GIF") return row;

      const liveGif = liveValues?.[index]?.mediaLink?.gifCreator || {};
      const gifState = {
        ...(row.mediaLink.gifCreator || {}),
        ...liveGif,
      };
      let generatedFile = getGifUploadFile(gifState.generatedFile);

      if (!generatedFile) {
        const sourceUrl = gifState.previewUrl || row.mediaLink?.url;
        if (isLocalGifPreviewUrl(sourceUrl)) {
          generatedFile = await fileFromGifSource(
            sourceUrl,
            gifState.fileName || `about-us-${index}.gif`
          );
        }
      }

      if (!generatedFile) return row;

      return {
        ...row,
        mediaLink: {
          ...row.mediaLink,
          gifCreator: {
            ...gifState,
            generatedFile,
          },
        },
      };
    })
  );
};

export const formatData = (tabKey, values, updatedValues, id) => {
  values =
    values?.length > 0
      ? values?.filter(function (entry) {
        return entry === "" ? entry.trim() !== "" : entry;
      })
      : [];

  if (vacancyKeys.includes(tabKey) && values.length > 0) {
    if (Object.keys(values[0]).length >= 2) {
    } else {
      values = [];
    }
  }

  if (values && values.length > 0) {
    values = values.filter(function (element) {
      return element !== undefined;
    });


    switch (tabKey) {
      case TAB_KEYS.corousel:
        return getTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.product:
        return getTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.service:
        return getTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.contract:
        return getContractTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.workExperience:
        return getTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.apprenticeship:
        return getVacancyTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.vacancy:
        return getVacancyTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.whyWorkForUs:
        return getTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.teamsAndRoles:
        return getTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.courses:
        return getTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.businessCard:
        return getTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.news:
        return getNewsTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.press:
        return getNewsTempValues({ tabKey, values, updatedValues, id });
      case TAB_KEYS.events:
        return getEventsTempValues({ tabKey, values, updatedValues, id });
      case contactUSkeys.sales:
        return getContactUsTempValues({ tabKey, values, updatedValues, id });
      case contactUSkeys.hr:
        return getContactUsTempValues({ tabKey, values, updatedValues, id });
      case contactUSkeys.eow:
        return getContactUsTempValues({ tabKey, values, updatedValues, id });
      case contactUSkeys.socialMedia:
        return getContactUsSocialMediaTempValues({ tabKey, values, updatedValues, id });
      case contactUSkeys.address:
        return getContactUsAddressTempValues({ tabKey, values, updatedValues, id });
      case aboutUSkeys.About:
        return getTempValues({ tabKey, values, updatedValues, aboutUsEnum: aboutUSkeys.About, id });
      case aboutUSkeys["Our Story"]:
        return getTempValues({ tabKey, values, updatedValues, aboutUsEnum: aboutUSkeys["Our Story"], id });
      case aboutUSkeys["Our Team"]:
        return getOurTeamTempValues({ tabKey, values, updatedValues, aboutUsEnum: aboutUSkeys["Our Team"], id });
      case aboutUSkeys["Our Client"]:
        return getOurTeamTempValues({ tabKey, values, updatedValues, aboutUsEnum: aboutUSkeys["Our Client"], id });
      case aboutUSkeys.Accreditation:
        return getAccreditationTempValues({ tabKey, values, updatedValues, id });
      case aboutUSkeys["Our Supporter"]:
        return getOurTeamTempValues({ tabKey, values, updatedValues, aboutUsEnum: aboutUSkeys["Our Supporter"], id });
      default:
        return {};
    }
  } else {
    return {};
  }
};

const getSortedContractData = (modifiedValues, updatedDataIds, tabKey) => {
  let items = {};

  if (modifiedValues?.length > 0 && tabKey === contract) {
    const freshItems = modifiedValues.filter(d => d.id).length > 0 ? [] : modifiedValues;

    if (freshItems.length > 0) {
      items["newData"] = getContractFormData(freshItems);
    } else {
      const modify = modifiedValues.filter(d => !d.id);
      if (modify.length > 0) {
        items["extraData"] = getContractFormData(modify);
      }

      const edited = modifiedValues.filter((d, i) =>
        (updatedDataIds?.includes(i) || hasGifBinaryToUpload(d)) && d.id
      );
      if (edited.length > 0) {
        items["editedData"] = getContractFormData(edited);
      }
    }
  }
  return items;
};

const usesRowId = (tabKey) =>
  vacancyKeys.includes(tabKey) || contract === tabKey || TAB_KEYS.events === tabKey;

const getSortedData = (modifiedValues, updatedDataIds, tabKey) => {
  console.log('get soretd data')
  var items = {};
  if (modifiedValues && modifiedValues.length > 0) {
    const freshItems =
      tabKey === "courses" ||
        tabKey === "why-work" ||
        tabKey === "teams-and-roles" ||
        tabKey === "product"
        ? modifiedValues?.filter((ele) => !ele?.sectionBlockId)
        : modifiedValues.filter((d) =>
          usesRowId(tabKey)
            ? d.id
            : d.sectionBlockId
        ).length > 0
          ? []
          : modifiedValues;
    if (freshItems.length > 0) {
      items["newData"] = vacancyKeys.includes(tabKey)
        ? getVacancyFormData(freshItems)
        : contract === tabKey
          ? getContractFormData(freshItems)
          :TAB_KEYS.events === tabKey ? getEventFormData(freshItems) :  getFormData(freshItems);
    } else {
      const modify = modifiedValues.filter(
        (d) =>
          !(usesRowId(tabKey)
            ? d.id
            : d.sectionBlockId)
      );
      if (modify && modify.length > 0) {
        items["extraData"] = vacancyKeys.includes(tabKey)
          ? getVacancyFormData(modify)
          : contract === tabKey
            ? getContractFormData(modify)
            : TAB_KEYS.events === tabKey ? getEventFormData(modify) : getFormData(modify);
      }
      const tempD = [];
      modifiedValues.forEach((d, i) => {
        const isTrackedEdit = updatedDataIds && updatedDataIds.includes(i);
        const isGifUpload = isOrgAdminGifTab(tabKey) && hasGifBinaryToUpload(d);
        if (
          (isTrackedEdit || isGifUpload) &&
          (usesRowId(tabKey)
            ? d.id
            : d.sectionBlockId)
        ) {
          tempD.push(d);
        }
      });
      if (tempD.length > 0) {
        items["editedData"] = vacancyKeys.includes(tabKey)
          ? getVacancyFormData(tempD)
          : contract === tabKey
            ? getContractFormData(tempD)
            : TAB_KEYS.events === tabKey ? getEventFormData(tempD) : getFormData(tempD);
      }
    }
  }
  return items;
};

const getFormData = (data) => {
  var formData = new FormData();
  const modifiedData = data.map((d) => {
    if (d.file) {
      formData.append("medias", d.file);
      delete d.file;
    }
    if (d.BrochureFile) {
      formData.append("file", d.BrochureFile);
      delete d.BrochureFile;
    }

    normalizeGifInputOnFormData(formData, d);
   
    return d;
  });
  const json = JSON.stringify(modifiedData);
  const blob = new Blob([json], {
    type: "application/json",
  
  });

  formData.append("micrositeDetailsInputs", blob);
  return formData;
};

const getEventFormData = (data) => {
  var formData = new FormData();
  console.log('get Event FormData',data)
  const modifiedData = data.map((d) => {
    console.log('formadat ddd evene ---',d)
    if (d.files) {
      formData.append("files", d.files);
      delete d.files;
    }
    if (d.logo) {
      formData.append("logo", d.logo);
      delete d.logo;
    }

    normalizeGifInputOnFormData(formData, d);

    return d;
  });
  const json = JSON.stringify(modifiedData);
  const blob = new Blob([json], {
    type: "application/json",
  
  });

  formData.append("eventTabInput", blob);
  return formData;
};



const formatAboutUs = (data) => {
  var formData = new FormData();
  const mediaMapping = { OUR_TEAM: 'images', OUR_CLIENT: 'images', ACCREDITATIONS_AWARDS: 'awardLogos', OUR_SUPPORTERS: 'images' }
  const modifiedData = data.map((d) => {
    const uploadingGif = hasGifBinaryToUpload(d);
    // Update + new GIF: do not also send leftover/original image binary on
    // `images`. Create still needs that field (see getOurTeamTempValues).
    if (d.blob && !(uploadingGif && d.sectionBlockId)) {
      formData.append(mediaMapping[d.aboutUsEnum], d.blob);
    }
    if (d.blob) {
      delete d.blob;
    }
    if (d?.gifInput) {
      const gifs = d.gifInput.gifs || [];
      let appendedGifFiles = 0;

      gifs.forEach((gifImg) => {
        const file = getGifUploadFile(gifImg) || getGifUploadFile(gifImg?.file);
        if (file) {
          formData.append("gifs", file);
          appendedGifFiles += 1;
        }
      });

      d.gifInput = {
        input: {
          mediaType: d.gifInput.input?.mediaType || "GIF",
          url: isStoredMediaUrl(d.gifInput.input?.url) ? d.gifInput.input.url : null,
          mediaProperty: d.gifInput.input?.mediaProperty ?? null,
        },
      };

      if (appendedGifFiles > 0) {
        // Same exclusive-or as getTempValues / normalizeGifInputOnFormData:
        // a GIF upload must not also carry image metadata.
        if (d.aboutUsTeamDataInput) {
          d.aboutUsTeamDataInput.image = null;
        }
      } else {
        // Update/edit without a new GIF file: gifInput must be null, not the
        // stored URL payload, so a previous GIF is not left incorrectly linked.
        d.gifInput = null;
      }
    }
    return d;
  });
  const json = JSON.stringify(modifiedData);
  const blob = new Blob([json], {
    type: "application/json",
  });
  formData.append("micrositeDetailsInputs", blob);
  return formData;
};

const getVacancyFormData = (data) => {
  var formData = new FormData();
  const modifiedData = data.map((d) => {
    if (d.file) {
      formData.append("files", d.file);
      delete d.file;
    }
    normalizeGifInputOnFormData(formData, d);
    return d;
  });
  const json = JSON.stringify(modifiedData);
  const blob = new Blob([json], {
    type: "application/json",
  });

  formData.append("vacancyDtoList", blob);

  return formData;
};

const getContractFormData = (data) => {
  var formData = new FormData();
  const modifiedData = data.map((d) => {
    if (d.file) {
      formData.append("files", d.file);
      delete d.file;
    }
    normalizeGifInputOnFormData(formData, d);
    return d;
  });
  const json = JSON.stringify(modifiedData);
  const blob = new Blob([json], {
    type: "application/json",
  });

  formData.append("contractsTabInputs", blob);

  return formData;
};

const getTempValues = ({ tabKey, values, updatedValues, aboutUsEnum, id }) => {
  const updatedDataIds = updatedValues?.[tabKey];
  if (values && values.length > 0) {
    const modifiedValues = values.map((d, i) => {
      if (d) {
        var temp = {};
        if (d && d.id) {
          temp.sectionBlockId = d.id;
        }
        temp.aboutUsEnum = aboutUsEnum
        temp.title = d.title ? d.title : "";
        if (d && d.assetTitleId) {
          temp.assetTitleId = d.assetTitleId ? d.assetTitleId : "titleID";
        }
        if (d && d.subTitle) {
          temp.subTitle = d.subTitle;
        }
        if (d && d.urlLink) {
          temp.urlLink = d.urlLink;
        }
        temp.description = d.description ? d.description : "";
        if (tabKey !== "businessCard") {
          temp.type = tabKey;
        }
        if (d && d.BrochureFile) {
          temp["BrochureFile"] = d.BrochureFile;
        }
        if (d && d.blob) {
          temp["file"] = d.blob;
        }
        if (d && d.mediaLink) {
          const mediaEx = d.mediaLink;
          if (mediaEx.mediaType === "IMAGE" && mediaEx.mediaProperty) {
            temp.media = {
              mediaProperty: mediaEx.mediaProperty,
              mediaType: "IMAGE",
            };
          } else if (mediaEx.mediaType === "SOCIAL_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "SOCIAL_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "VIMEO_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "VIMEO_MEDIA",
              url: mediaEx.url,
            };
          }
          else if (mediaEx.mediaType === "SYNTHESIA_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "SYNTHESIA_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "GIF") {
            applyGifMediaToTemp(temp, mediaEx, {
              useNullUnchanged: usesNullGifUnchangedPayload(tabKey, aboutUsEnum),
            });
          }
        }
        temp.tabConfigId = id
        temp.levelId = d.levelId;
        temp.title = d.title;
        return temp;
      }
      return undefined;
    });
    return getSortedData(modifiedValues, updatedDataIds, tabKey);
  } else {
    return {};
  }
};

const getOurTeamTempValues = ({ tabKey, values, updatedValues, aboutUsEnum, id }) => {
  const updatedDataIds = updatedValues[tabKey];

  if (values && values.length > 0) {
    const modifiedValues = values.map((d, i) => {
      if (d) {
        var temp = {
          "aboutUsEnum": "OUR_TEAM",
          "aboutUsTeamDataInput": {
            "image": {
              "mediaProperty": {
                "id": "",
                "name": "",
                "extension": "",
              },

              "url": "",
              "mediaType": ""
            },
            "bio": "",
          }
        }
        if (d && d.id) {
          temp.sectionBlockId = d.id;
        }
        temp["aboutUsEnum"] = aboutUsEnum
        if (d.blob) {
          temp["blob"] = d.blob
        }

        temp.aboutUsTeamDataInput.bio = d.bio

        if (d.mediaLink?.mediaType === "GIF") {
          const mediaEx = d.mediaLink;
          applyGifMediaToTemp(temp, mediaEx, {
            useNullUnchanged: usesNullGifUnchangedPayload(tabKey, aboutUsEnum),
          });
          const isExistingBlock = Boolean(d.id);
          const uploadingGif = hasGifBinaryToUpload(temp);
          if (uploadingGif && !isExistingBlock) {
            const gifState = mediaEx.gifCreator || {};
            temp.blob =
              getGifUploadFile(gifState.generatedFile) ||
              getGifUploadFile((mediaEx.gifImages || gifState.images || [])[0]?.file) ||
              getGifUploadFile((mediaEx.gifImages || gifState.images || [])[0]) ||
              temp.blob;
          } else if (isExistingBlock) {
            // Update path: GIF binaries go on `gifs` only. Drop leftover
            // photo files so we do not also send `images`.
            delete temp.blob;
          }
          if (!uploadingGif) {
            temp.gifInput = null;
          }
          // Editing GIF only — same exclusive-or as getTempValues (`media` stays unset).
          temp.aboutUsTeamDataInput.image = null;
        } else if (d.blob) {
          // Updating the image, not the GIF.
          temp.gifInput = null;
          temp.aboutUsTeamDataInput.image = {
            mediaProperty: {
              id: d.awardsLogos?.mediaProperty?.id || "",
              name: d.awardsLogos?.mediaProperty?.name || "",
              extension: d.awardsLogos?.mediaProperty?.extension || "",

            },
            url: d.mediaLink?.url || "",  // 👈 IMPORTANT FIX
            mediaType: d.awardsLogos?.mediaType || d.mediaLink?.mediaType || "",
          };
        } else {
            temp.gifInput = null;
            if (d.mediaLink?.mediaType === "IMAGE") {
              temp.aboutUsTeamDataInput.image = d.awardsLogos
            } else if (
              d.mediaLink?.mediaType !== "IMAGE" &&
              d.mediaLink?.url?.toString().trim()
            ) {
              temp.aboutUsTeamDataInput.image = {
                url: d.mediaLink?.url,
                mediaType: d.mediaLink?.mediaType || "",
              };

            } else {
              delete temp.aboutUsTeamDataInput.image;
            }
          }

        temp.aboutUsTeamDataInput.linkedin = d.linkedin
        temp.aboutUsTeamDataInput.email = d.email
        temp.tabConfigId = id
        temp.description = d.description ? d.description : "";
        temp.title = d.title

        return temp;
      }
      return undefined;
    });
    return getSortedDataForAboutUs(modifiedValues, updatedDataIds, tabKey);
  } else {
    return {};
  }
}

const getAccreditationTempValues = ({ tabKey, values, updatedValues, id }) => {
  const updatedDataIds = updatedValues?.[tabKey];
  if (values && values.length > 0) {
    const modifiedValues = values.map((d, i) => {
      if (d) {
        var temp = { ...d };
        if (d && d.id) {
          temp.sectionBlockId = d.id;
        }
        temp.aboutUsEnum = "ACCREDITATIONS_AWARDS"
        if (d.blob) {
          temp["blob"] = d.blob
        }
        if (temp.awardsLogos && !temp.awardsLogos.mediaProperty?.name) {
          delete temp.awardsLogos;
        }
        temp.tabConfigId = id

        return temp;
      }
      return undefined;
    });
    return getSortedDataForAboutUs(modifiedValues, updatedDataIds, tabKey);
  } else {
    return {};
  }
};

const getNewsTempValues = ({ tabKey, values, updatedValues, id }) => {
  const updatedDataIds = updatedValues[tabKey];


  if (values && values.length > 0) {
    const modifiedValues = values.map((d, i) => {
      if (d) {
        var temp = {};
        if (d && d.id) {
          temp.sectionBlockId = d.id;
        }
        temp.title = d.title ? d.title : "";
        if (d && d.assetTitleId) {
          temp.assetTitleId = d.assetTitleId ? d.assetTitleId : "titleID";
        }
        if (d && d.urlLink) {
          temp.urlLink = d.urlLink;
        }
        temp.description = d.description ? d.description : "";
        if (tabKey !== "businessCard") {
          temp.type = tabKey;
        }
        if (d && d.blob) {
          temp["file"] = d.blob;
        }
        if (d && d.mediaLink) {
          const mediaEx = d.mediaLink;
          if (mediaEx.mediaType === "IMAGE" && mediaEx.mediaProperty) {
            temp.media = {
              mediaProperty: mediaEx.mediaProperty,
              mediaType: "IMAGE",
            };
          } else if (mediaEx.mediaType === "SOCIAL_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "SOCIAL_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "VIMEO_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "VIMEO_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "SYNTHESIA_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "SYNTHESIA_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "GIF") {
            applyGifMediaToTemp(temp, mediaEx, {
              useNullUnchanged: usesNullGifUnchangedPayload(tabKey),
            });
          }
        }
        temp.levelId = d.levelId;
        temp.tabConfigId = id;

        return temp;
      }
      return undefined;
    });

    return getSortedData(modifiedValues, updatedDataIds, tabKey);
  } else {
    return {};
  }
};

const getVacancyTempValues = ({ tabKey, values, updatedValues, id }) => {
  const updatedDataIds = updatedValues[tabKey];
  const descriptionItems = [TAB_KEYS.apprenticeship, TAB_KEYS.vacancy];

  if (values && values.length > 0) {
    const modifiedValues = values.map((d, i) => {
      if (d) {
        var temp = {};
        if (d && d.id) {
          temp.id = d.id;
        }
        temp.apprenticeshipType = tabKey;
        temp.title = d.title;
        temp.jobTypeEnum = d.jobTypeEnum;
        temp.compensationType = "ANNUALLY";
        temp.compensation = d.compensation;
        temp.closingDate = moment(new Date(d.closingDate)).format("YYYY-MM-DD");
        temp.description = descriptionItems.includes(tabKey)
          ? d.description
          : "description";
        // temp.type = tabKey
        temp.skillIds = d.skillIds;
        temp.urlLink = d.urlLink;
        temp.tabConfigId = id

        if (d && d.blob) {
          temp["file"] = d.blob;
        }
        if (d && d.mediaLink) {
          const mediaEx = d.mediaLink;
          if (mediaEx.mediaType === "IMAGE" && mediaEx.mediaProperty) {
            temp.media = {
              mediaProperty: mediaEx.mediaProperty,
              mediaType: "IMAGE",
            };
          } else if (mediaEx.mediaType === "SOCIAL_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "SOCIAL_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "VIMEO_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "VIMEO_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "SYNTHESIA_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "SYNTHESIA_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "GIF") {
            applyGifMediaToTemp(temp, mediaEx, {
              useNullUnchanged: usesNullGifUnchangedPayload(tabKey),
            });
          }
        }
        temp.levelId = d.levelId;
        return temp;
      }
      return undefined;
    });

    return getSortedData(modifiedValues, updatedDataIds, tabKey);
  } else {
    return {};
  }
};

const getContractTempValues = ({ tabKey, values, updatedValues, id }) => {
  const updatedDataIds = updatedValues[tabKey];

  if (values && values.length > 0) {
    const modifiedValues = values.map((d, i) => {
      if (d) {
        var temp = {};
        if (d && d.id) {
          temp.id = d.id;
        }
        temp.title = d.title;
        temp.procurementReference = d.procurementReference;
        temp.postalCode = d.postalCode;
        temp.valueOfContract = d.valueOfContract;
        temp.description = d.description;
        if (d.closeTime) {
          temp.closeDate = moment(new Date(d.closeDate)).format("YYYY-MM-DD");
        }
        if (d.startDate) {
          temp.startDate = moment(new Date(d.startDate)).format("YYYY-MM-DD");
        }
        if (d.endDate) {
          temp.endDate = moment(new Date(d.endDate)).format("YYYY-MM-DD");
        }
        if (d.closeTime) {
          temp.closeTime = moment(new Date(d.closeTime)).format(
            "YYYY-MM-DDTHH:mm:ss.SSSZ"
          );
        }
        temp.contractType = d.contractType;
        temp.webLink = d.webLink;
        temp.email = d.email;
        temp.tabConfigId = id

        if (d && d.blob) {
          temp["file"] = d.blob;
        }
        if (d && d.mediaLink) {
          const mediaEx = d.mediaLink;
          if (mediaEx.mediaType === "IMAGE" && mediaEx.mediaProperty) {
            temp.media = {
              mediaProperty: mediaEx.mediaProperty,
              mediaType: "IMAGE",
            };
          } else if (mediaEx.mediaType === "SOCIAL_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "SOCIAL_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "VIMEO_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "VIMEO_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "SYNTHESIA_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "SYNTHESIA_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "GIF") {
            applyGifMediaToTemp(temp, mediaEx, {
              useNullUnchanged: usesNullGifUnchangedPayload(tabKey),
            });
          }
        }
        return temp;
      }
      return undefined;
    });

    let temp = getSortedContractData(modifiedValues, updatedDataIds, tabKey)
    return temp
  } else {
    return {};
  }
};

const getContactUsTempValues = ({ tabKey, values, updatedValues, id }) => {
  const updatedDataIds = updatedValues?.[tabKey];
  const modifiedValues = values?.map((d, i) => {
    if (d) {
      var temp = {};
      if (d && d.id) {
        temp.sectionBlockId = d.id;
      }
      // values.address.replace(/(\r\n|\n|\r)/g, ' ');
      temp.contactUsId = d.id
      temp.contactUsTypeEnum = tabKey;
      temp.phone = d.phone;
      temp.chatNumber = d.chatNumber;
      temp.websiteLink = d.websiteLink;
      // temp.postalAddress = d?.postalAddress?.replace(/(\r\n|\n|\r)/g, " ") || '';
      temp.email = d.email;
      temp.tabConfigId = id

      return temp;
    }
    return undefined;
  });

  return getSortedDataForContactUs(modifiedValues, updatedDataIds, tabKey);
};

const getContactUsSocialMediaTempValues = ({ tabKey, values, updatedValues, id }) => {
  const updatedDataIds = updatedValues[tabKey];

  const modifiedValues = values.map((d, i) => {
    if (d) {
      var temp = {};
      if (d && d.id) {
        temp.sectionBlockId = d.id;
      }
      temp.contactUsId = d.id
      temp.contactUsTypeEnum = tabKey;
      temp.linkedInLink = d.linkedInLink;
      temp.youtubeLink = d.youtubeLink;
      temp.instagramLink = d.instagramLink;
      temp.facebookLink = d.facebookLink;
      temp.tiktokLink = d.tiktokLink;
      temp.twitterLink = d.twitterLink;
      temp.tabConfigId = id

      return temp;
    }
    return undefined;
  });

  return getSortedDataForContactUs(modifiedValues, updatedDataIds, tabKey);
};
const getContactUsAddressTempValues = ({ tabKey, values, updatedValues, id }) => {
  const updatedDataIds = updatedValues[tabKey];

  const modifiedValues = values.map((d, i) => {
    if (d) {
      var temp = {};
      if (d && d.id) {
        temp.sectionBlockId = d.id;
      }
      temp.contactUsTypeEnum = tabKey;
      temp.addressInputs = [
        {
          "street": d.street,
          "postalCode": d.postalCode
        }
      ]
      // temp.tabConfigId = id
      return temp;
    }
    return undefined;
  });
  return getSortedDataForContactUsAddress({ modifiedValues, updatedDataIds, tabKey, id });
};

const getSortedDataForContactUs = (modifiedValues, updatedDataIds, tabKey) => {
  var items = {};

  if (modifiedValues && modifiedValues.length > 0) {
    const freshItems =
      modifiedValues.filter((d) => d.sectionBlockId).length > 0
        ? []
        : modifiedValues;
    if (freshItems.length > 0) {
      items["newData"] = freshItems;
    } else {
      const modify = modifiedValues.filter((d) => !d.sectionBlockId);
      if (modify && modify.length > 0) {
        items["extraData"] = modify;
      }
      const tempD = [];
      modifiedValues.forEach((d, i) => {
        if (updatedDataIds && updatedDataIds.includes(i) && d.sectionBlockId) {
          tempD.push(d);
        }
      });
      if (tempD.length > 0) {
        items["editedData"] = tempD;
      }
    }
  }
  return items;
};

const getSortedDataForContactUsAddress = ({ modifiedValues, updatedDataIds, tabKey, id }) => {
  var items = { newData: [], editedData: [] };

  modifiedValues.forEach((ele, i) => {
    if (!ele.sectionBlockId) {
      items['newData'].push({
        contactUsTypeEnum: ele.contactUsTypeEnum,
        addressInputs: ele.addressInputs,
        // tabConfigId: id
      })
    }
    if (ele.sectionBlockId && updatedDataIds && updatedDataIds.length > 0 && updatedDataIds.includes(i)) {
      items['editedData'].push({
        contactUsId: ele.sectionBlockId,
        contactUsTypeEnum: ele.contactUsTypeEnum,
        addressInputs: ele.addressInputs,
        // tabConfigId: id
      })
    }
  })

  if (items.newData.length === 0) {
    delete items.newData
  }
  if (items.editedData.length === 0) {
    delete items.editedData
  }
  return items;
};

const getSortedDataForAboutUs = (modifiedValues, updatedDataIds, tabKey) => {
  var items = { newData: [], editedData: [] };

  modifiedValues.forEach((ele, i) => {
    if (!ele.sectionBlockId) {
      items['newData'].push(ele)
    }
    const isTrackedEdit =
      ele.sectionBlockId &&
      updatedDataIds &&
      updatedDataIds.length > 0 &&
      updatedDataIds.includes(i);
    const isGifUpload = ele.sectionBlockId && hasGifBinaryToUpload(ele);
    if (isTrackedEdit || isGifUpload) {
      items['editedData'].push(ele)
    }
  })
  if (items.newData.length === 0) {
    delete items.newData
  }
  if (items.editedData.length === 0) {
    delete items.editedData
  }

  if (items['newData'])
    items['newData'] = formatAboutUs(items['newData'])

  if (items['editedData'])
    items['editedData'] = formatAboutUs(items['editedData'])
  return items;
};

const getEventsTempValues = ({ tabKey, values, updatedValues, id }) => {
  const updatedDataIds = updatedValues?.[tabKey];

  if (values && values.length > 0) {
    const modifiedValues = values.map((d, i) => {
      if (d) {
        var temp = {};
        if (d && d.id) {
          temp.id = d.id;
        }
        temp.eventName = d.eventName ? d.eventName : "";
        temp.description = d.description ? d.description : "";
        temp.moreInfo = d.moreInfo ? d.moreInfo : "";
        temp.address = d.address ? d.address : "";
        temp.venueLink = d.venueLink ? d.venueLink : "";
        temp.webLink = d.webLink ? d.webLink : "";
        temp.country = d.country ? d.country : "";
        temp.startDate = d.startDate ? moment(new Date(d.startDate)).format("YYYY-MM-DD") : "";
        temp.endDate = d.endDate ? moment(new Date(d.endDate)).format("YYYY-MM-DD") : "";

        if (d && d.logoBlob) {
          temp["logo"] = d.logoBlob;
        }
          if (d && d.blob) {
          temp["files"] = d.blob;
        }

        if (d?.eventLogo?.mediaType === "IMAGE" && d?.eventLogo?.mediaProperty) {
          temp.eventLogo = {
            mediaProperty: d.eventLogo.mediaProperty,
            mediaType: "IMAGE",
          };
        }

        if (d && d.mediaLink) {

          const mediaEx = d.mediaLink;

          if (mediaEx.mediaType === "IMAGE" && mediaEx.mediaProperty) {
            temp.media = {
              mediaProperty: mediaEx.mediaProperty,
              mediaType: "IMAGE",
            };

        
          }
            
           else if (mediaEx.mediaType === "SOCIAL_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "SOCIAL_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "VIMEO_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "VIMEO_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "SYNTHESIA_MEDIA" && mediaEx.url) {
            temp.media = {
              mediaType: "SYNTHESIA_MEDIA",
              url: mediaEx.url,
            };
          } else if (mediaEx.mediaType === "GIF") {
            applyGifMediaToTemp(temp, mediaEx, {
              useNullUnchanged: usesNullGifUnchangedPayload(tabKey),
            });
          }
        }

        temp.tabConfigId = id;

        return temp;
      }
      return undefined;
    });

    return getSortedData(modifiedValues, updatedDataIds, tabKey);
  } else {
    return {};
  }
};